import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { Button, cnMixSpace } from 'uikit';
import { newDate } from 'utils';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getWorkOrderStatusLabel } from '../workOrderConstants';
import { gqlWorkOrder } from '../workOrderQueries';

import { WorkOrderRequisitesUpdate } from './WorkOrderRequisitesUpdate';

interface IWorkOrderRequisitesProps {
    workOrderId: number;
    onChange: (callVersionsRenew: boolean) => void;
    isDisabled: boolean;
    withButton?: boolean;
    isOpenOuter?: boolean;
    refBtn?: React.RefObject<HTMLButtonElement>;
    setIsOpenOuter?: React.Dispatch<React.SetStateAction<boolean>>;
    orderRules: IUseAccess['order'];
    projectId?: string;
    status?: WorkOrderStatusEnum;
}

export interface IWorkOrderData extends Omit<WorkOrderData, 'signingDate' | 'startDate' | 'finishDate' | 'status'> {
    signingDate?: Date | null;
    startDate?: Date | null;
    finishDate?: Date | null;
    status: { id: WorkOrderStatusEnum; label: string };
}

export const parseWorkOrder = R.evolve({
    signingDate: newDate,
    startDate: newDate,
    finishDate: newDate,
    status: (id: WorkOrderStatusEnum) => ({ id, label: getWorkOrderStatusLabel(id) }),
});

export const WorkOrderRequisites: React.FC<IWorkOrderRequisitesProps> = (props) => {
    const navigate = useNavigate();
    const {
        workOrderId,
        onChange,
        isDisabled,
        withButton = true,
        isOpenOuter,
        setIsOpenOuter,
        refBtn,
        orderRules,
        projectId,
        status,
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const variables = useMemo(() => ({ workOrderId }), [workOrderId]);

    const [workOrderResponse, workOrderState] = useRequest({
        requestId: 'workOrder',
        query: gqlWorkOrder,
        variables,
        withEffect: isOpenOuter || isOpen,
    });

    const defaultValues = useMemo(
        () => workOrderResponse?.uerWorkOrder?.data && parseWorkOrder(workOrderResponse?.uerWorkOrder?.data),
        [workOrderResponse],
    );

    const isRequisitesReadOnly = useMemo(() => status === WorkOrderStatusEnum.Completed, [status]);
    const isStructureReadOnly = useMemo(
        () => [WorkOrderStatusEnum.Completed, WorkOrderStatusEnum.Delayed].includes(status as WorkOrderStatusEnum),
        [status],
    );

    const handleOnButtonClick = useCallback(() => {
        setIsOpen(true);
    }, []);

    const handleOnEditCancel = useCallback(() => {
        if (setIsOpenOuter) {
            setIsOpenOuter(false);
        } else {
            setIsOpen(false);
        }
    }, [setIsOpenOuter]);

    const handleOnEditConfirm = useCallback(
        (callVersionsRenew: boolean) => {
            if (setIsOpenOuter) {
                setIsOpenOuter(false);
            } else {
                setIsOpen(false);
            }
            onChange(callVersionsRenew);
        },
        [onChange, setIsOpenOuter],
    );

    const onEditStructureClick = useCallback(() => {
        navigate(`/projects/uer/${projectId}/order/${workOrderId}/structure`);
    }, [navigate, projectId, workOrderId]);

    return (
        <>
            {withButton && (
                <>
                    <Button
                        disabled={isDisabled}
                        className={cnMixSpace({ mR: 'xs' })}
                        label={isRequisitesReadOnly ? 'Просмотреть данные' : 'Редактировать данные'}
                        view="secondary"
                        size="s"
                        onClick={handleOnButtonClick}
                        loading={workOrderState === RequestStatus.FETCHING}
                        ref={refBtn}
                    />
                    <Button
                        label={isStructureReadOnly ? 'Просмотреть структуру' : 'Редактировать структуру'}
                        size="s"
                        view="secondary"
                        className={cnMixSpace({ mR: 'xs' })}
                        onClick={onEditStructureClick}
                    />
                </>
            )}
            {(isOpenOuter || isOpen) && workOrderState === RequestStatus.SUCCESS && (
                <WorkOrderRequisitesUpdate
                    defaultValues={defaultValues as IWorkOrderData}
                    workOrderStatus={workOrderResponse?.uerWorkOrder?.data?.status}
                    onConfirm={handleOnEditConfirm}
                    onCancel={handleOnEditCancel}
                    orderRules={orderRules}
                />
            )}
        </>
    );
};

WorkOrderRequisites.displayName = 'WorkOrderRequisites';
